"use client";

import { NextIntlClientProvider } from "next-intl";
import * as React from "react";
import { latnToCyrl } from "./transliterator";
import { FlatI18nProvider, type FlatBundles } from "./flat-keys-provider";

export type SupportedLocale = "sr-Latn" | "sr-Cyrl" | "en";

/**
 * Recursive message tree compatible with `next-intl`'s `AbstractIntlMessages`.
 * Each key maps to a leaf string or a nested namespace.
 */
export type MessageTree = { [key: string]: string | MessageTree };

interface I18nContextValue {
  locale: SupportedLocale;
  setLocale: (next: SupportedLocale) => void;
}

const I18nContext = React.createContext<I18nContextValue | null>(null);

const STORAGE_KEY = "aio:locale";

interface MessageBundle {
  "sr-Latn": MessageTree;
  en: MessageTree;
}

function deepMap(value: MessageTree, fn: (s: string) => string): MessageTree {
  const out: MessageTree = {};
  for (const [k, v] of Object.entries(value)) {
    out[k] = typeof v === "string" ? fn(v) : deepMap(v, fn);
  }
  return out;
}

export interface I18nProviderProps {
  children: React.ReactNode;
  bundles: MessageBundle;
  /**
   * Module-level flat-key bundles. Keyed on `<moduleId>.<entity>.<field>`
   * paths that next-intl can't represent because both `fleet.machine` and
   * `fleet.machine.x` exist simultaneously. Resolved through `useFlatT()`
   * inside the substrate's auto-page client components. Optional — when
   * omitted, all flat-key lookups fall back to the raw key.
   */
  flatBundles?: FlatBundles;
  defaultLocale?: SupportedLocale;
  timeZone?: string;
}

export function I18nProvider({
  children,
  bundles,
  flatBundles,
  defaultLocale = "sr-Latn",
  timeZone = "Europe/Belgrade",
}: I18nProviderProps) {
  const [locale, setLocaleState] = React.useState<SupportedLocale>(() => {
    if (typeof window === "undefined") return defaultLocale;
    const stored = localStorage.getItem(STORAGE_KEY) as SupportedLocale | null;
    return stored ?? defaultLocale;
  });

  const setLocale = React.useCallback((next: SupportedLocale) => {
    setLocaleState(next);
    localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const messages = React.useMemo<MessageTree>(() => {
    if (locale === "en") return bundles.en;
    if (locale === "sr-Latn") return bundles["sr-Latn"];
    // sr-Cyrl: derive from sr-Latn at runtime
    return deepMap(bundles["sr-Latn"], latnToCyrl);
  }, [locale, bundles]);

  const value = React.useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  return (
    <I18nContext.Provider value={value}>
      <NextIntlClientProvider
        locale={locale}
        messages={messages}
        timeZone={timeZone}
        // Missing keys are a substrate-level signal: when a module's bundle
        // doesn't have an entry yet, the page should still render with the
        // raw key (it's recognisable, not a crash). next-intl's default
        // throws in production; we explicitly opt into "log + fallback to
        // raw key" so unfilled bundles degrade visibly without breaking
        // the page.
        onError={(error) => {
          if (error.code === "MISSING_MESSAGE") return;
          if (typeof console !== "undefined") console.error(error);
        }}
        getMessageFallback={({ key }) => key}
      >
        <FlatI18nProvider locale={locale} bundles={flatBundles ?? {}}>
          {children}
        </FlatI18nProvider>
      </NextIntlClientProvider>
    </I18nContext.Provider>
  );
}

export function useLocale(): I18nContextValue {
  const ctx = React.useContext(I18nContext);
  if (!ctx) throw new Error("useLocale must be used inside <I18nProvider>");
  return ctx;
}
